import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { setStudentToken } from "../lib/studentAuth";
import ShareWithFriend from "../components/ShareWithFriend";

const CourseSuccess = () => {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const navigate = useNavigate();
    const [status, setStatus] = useState("polling");
    const [purchaseEmail, setPurchaseEmail] = useState("");
    const [purchaseCourseId, setPurchaseCourseId] = useState("");
    const [first_name, setFirstName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Poll Stripe checkout status until paid or timeout (max 5 attempts × 2s = 10s)
    useEffect(() => {
        if (!sessionId) {
            setStatus("missing");
            return;
        }
        let attempts = 0;
        const max = 8;
        const tick = async () => {
            attempts += 1;
            try {
                const r = await api.get(`/payments/checkout/status/${sessionId}`);
                if (r.data.payment_status === "paid") {
                    setStatus("paid");
                    setPurchaseEmail(r.data.email || "");
                    setEmail(r.data.email || "");
                    setPurchaseCourseId(r.data.course_id || "");
                    return;
                }
                if (r.data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                if (attempts >= max) {
                    setStatus("timeout");
                    return;
                }
                setTimeout(tick, 2000);
            } catch (e) {
                if (attempts >= max) {
                    setStatus("error");
                    return;
                }
                setTimeout(tick, 2000);
            }
        };
        tick();
    }, [sessionId]);

    const submitActivation = async (e) => {
        e.preventDefault();
        if (!email || !password || password.length < 8) return;
        setSubmitting(true);
        try {
            const r = await api.post("/students/activate", {
                email: email.trim().toLowerCase(),
                password,
                first_name: first_name.trim(),
                session_id: sessionId,
            });
            setStudentToken(r.data.token);
            toast.success("Account created. Opening your course...");
            // Route mini-course buyers to the mini-course; everyone else to the full dashboard.
            const courseId = r.data.course_id || purchaseCourseId;
            if (courseId === "daily-battle-drill") {
                navigate("/daily-battle-drill");
            } else {
                navigate("/course/dashboard");
            }
        } catch (err) {
            const msg = err.response?.data?.detail || "Activation failed. Try again.";
            toast.error(typeof msg === "string" ? msg : "Activation failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-black text-white min-h-screen flex items-center justify-center px-6 py-20" data-testid="course-success-page">
            <div className="w-full max-w-lg">
                {status === "polling" && (
                    <div className="text-center" data-testid="course-success-polling">
                        <div className="font-stencil tracking-widest text-xs text-red-600 mb-6">[ CONFIRMING YOUR PAYMENT ]</div>
                        <div className="font-display text-3xl uppercase tracking-tight">Hold on a moment...</div>
                        <div className="mt-4 text-white/55 text-sm">Hang tight — Stripe usually finalises in a couple of seconds.</div>
                    </div>
                )}
                {status === "paid" && (
                    <div data-testid="course-success-paid">
                        <div className="text-center mb-10">
                            <CheckCircle2 size={56} className="text-red-600 mx-auto" strokeWidth={1.5} />
                            <div className="mt-4 font-stencil tracking-widest text-xs text-red-600">[ PAYMENT CONFIRMED ]</div>
                            <h1 className="mt-4 font-display text-4xl md:text-5xl uppercase tracking-tighter leading-[0.9]">
                                You're <span className="text-red-600">in.</span>
                            </h1>
                            <p className="mt-4 text-white/70">
                                Create your password to open the course.
                            </p>
                        </div>
                        <form onSubmit={submitActivation} className="space-y-3 border border-white/10 bg-[#0a0a0a] p-8" data-testid="activate-form">
                            <input
                                type="text"
                                placeholder="First name"
                                value={first_name}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                                className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                data-testid="activate-first-name"
                                autoComplete="given-name"
                            />
                            <input
                                type="email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={!!purchaseEmail}
                                className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30 disabled:opacity-60"
                                data-testid="activate-email"
                                autoComplete="email"
                            />
                            <input
                                type="password"
                                placeholder="Choose a password (8+ characters)"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={8}
                                className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                data-testid="activate-password"
                                autoComplete="new-password"
                            />
                            <button type="submit" disabled={submitting} className="w-full btn-primary justify-center disabled:opacity-50" data-testid="activate-submit">
                                <Sparkles size={16} /> {submitting ? "Setting up..." : "Create account & open course"}
                            </button>
                        </form>
                        {/* Encourage referrals while the buyer is at peak excitement (right after they paid) */}
                        {purchaseCourseId === "daily-battle-drill" && (
                            <ShareWithFriend senderFirstName={first_name} />
                        )}
                    </div>
                )}
                {status === "timeout" && (
                    <div className="text-center" data-testid="course-success-timeout">
                        <div className="font-display text-2xl uppercase tracking-tight">Still confirming with Stripe...</div>
                        <p className="mt-4 text-white/60 text-sm">This sometimes takes an extra moment. Refresh in 30 seconds — your payment is safe and your access will appear.</p>
                        <button onClick={() => window.location.reload()} className="mt-6 btn-ghost text-xs">Refresh</button>
                    </div>
                )}
                {(status === "expired" || status === "error" || status === "missing") && (
                    <div className="text-center" data-testid="course-success-error">
                        <div className="font-display text-2xl uppercase tracking-tight">Something went sideways</div>
                        <p className="mt-4 text-white/60 text-sm">Your card wasn't charged. Try again from the course page.</p>
                        <Link to="/course" className="mt-6 btn-ghost text-xs inline-flex">← Back to course</Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CourseSuccess;
